import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "bgColor", "radius", "style", "testId"],
  _excluded2 = ["children", "pulse"],
  _excluded3 = ["children", "pulse", "testId"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx, keyframes } from '@emotion/react';
import { reduceMotionAsPerUserPreference } from '@atlaskit/motion';
import { P300 } from '@atlaskit/theme/colors';
// NOTE:
// Pulse color "rgb(101, 84, 192)" derived from "colors.P300"
var baseShadow = "0 0 0 2px ".concat("var(--ds-border-discovery, ".concat(P300, ")"));
var easing = 'cubic-bezier(0.55, 0.055, 0.675, 0.19)';
var pulseKeyframes = keyframes({
  '0%, 33%': {
    boxShadow: "".concat(baseShadow, ", 0 0 0 ", "var(--ds-border-discovery, rgba(101, 84, 192, 1))")
  },
  '66%, 100%': {
    boxShadow: "".concat(baseShadow, ", 0 0 0 10px rgba(101, 84, 192, 0.01)")
  }
});
// This is needed for unit tests
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export var pulseKeyframesName = pulseKeyframes.name;
var animationStyles = css({
  animation: "".concat(pulseKeyframes, " 3000ms ").concat(easing, " infinite"),
  // Keep a purple boxshadow on the Pulse component if `prefers-reduced-motion`
  // is applied so we still have a functioning semantic affordance.
  boxShadow: baseShadow
});
var Base = function Base(_ref) {
  var children = _ref.children,
    bgColor = _ref.bgColor,
    radius = _ref.radius,
    style = _ref.style,
    testId = _ref.testId,
    props = _objectWithoutProperties(_ref, _excluded);
  return jsx("div", _extends({
    "data-testid": testId
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
    ,
    style: _objectSpread(_objectSpread({}, style), {}, {
      backgroundColor: bgColor,
      borderRadius: radius ? "".concat(radius, "px") : undefined
    })
    // eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props
  }, props), children);
};

/**
 * __Target inner__
 *
 * Used to apply spotlight border and pulse to spotlight targets.
 *
 * @internal
 */
export var TargetInner = function TargetInner(_ref2) {
  var children = _ref2.children,
    pulse = _ref2.pulse,
    props = _objectWithoutProperties(_ref2, _excluded2);
  return jsx(Base
  // eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props
  , _extends({}, props, {
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    css: [pulse && animationStyles, reduceMotionAsPerUserPreference]
  }), children);
};
var targetOverlayStyles = css({
  width: '100%',
  height: '100%',
  position: 'absolute',
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  insetBlockStart: 0,
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  insetInlineStart: 0
});

/**
 * __Target overlay__
 *
 * Overlays a spotlight target, allowing for custom click events that are intended
 * only for onboarding.
 *
 * @internal
 */
export var TargetOverlay = function TargetOverlay(props) {
  return jsx("div", _extends({}, props, {
    css: targetOverlayStyles
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
    ,
    style: {
      cursor: props.onClick ? 'pointer' : 'auto'
    }
  }));
};

/**
 * __Spotlight pulse__
 *
 * A spotlight pulse draws attention to a new feature.
 *
 * - [Examples](https://atlassian.design/components/onboarding/examples)
 * - [Code](https://atlassian.design/components/onboarding/code)
 * - [Usage](https://atlassian.design/components/onboarding/usage)
 */
export var Pulse = function Pulse(_ref3) {
  var children = _ref3.children,
    _ref3$pulse = _ref3.pulse,
    pulse = _ref3$pulse === void 0 ? true : _ref3$pulse,
    testId = _ref3.testId,
    props = _objectWithoutProperties(_ref3, _excluded3);
  return jsx(Base
  // eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props
  , _extends({}, props, {
    css: [pulse && animationStyles,
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    reduceMotionAsPerUserPreference],
    testId: testId
  }), children);
};